import { Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useFavoriteToggle } from '@/features/favorites/use-favorites'
import { cn } from '@/lib/utils'

interface FavoriteHeartButtonProps {
  projectId: string
  isFavoritedByMe: boolean
  /** Optional inline count (Project Detail header). Leave null to render no count (browse cards). */
  favoriteCount?: number | null
  /** Style hint — `'filled'` for Project Detail header chrome, `'overlay'` for browse cards. */
  variant?: 'filled' | 'overlay'
  /** Override label for accessibility / testing. */
  className?: string
}

export const FavoriteHeartButton = ({
  projectId,
  isFavoritedByMe,
  favoriteCount,
  variant = 'filled',
  className,
}: FavoriteHeartButtonProps) => {
  const toggle = useFavoriteToggle()

  const handleClick = (e: React.MouseEvent) => {
    // Critical for browse cards — the card itself is a link to /p/:handle/:slug.
    e.preventDefault()
    e.stopPropagation()
    toggle.mutate({ projectId, currentlyFavorited: isFavoritedByMe })
  }

  const label = isFavoritedByMe ? 'Unfavorite project' : 'Favorite project'

  return (
    <Button
      variant={variant === 'overlay' ? 'secondary' : 'ghost'}
      size="sm"
      onClick={handleClick}
      disabled={toggle.isPending}
      aria-label={label}
      aria-pressed={isFavoritedByMe}
      className={cn('gap-1.5', variant === 'overlay' && 'h-8 px-2', className)}
    >
      <Heart
        className={cn(
          'h-4 w-4 transition-colors',
          isFavoritedByMe ? 'fill-rose-500 text-rose-500' : 'text-muted-foreground'
        )}
        aria-hidden
      />
      {favoriteCount != null && (
        <span className="text-sm tabular-nums text-muted-foreground">{favoriteCount}</span>
      )}
    </Button>
  )
}
